
import { Component } from 'vue-property-decorator';
import TableFilterable from '@/components/reports-v3/components/filterables/TableFilterable.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import SelectFilter from '@/components/reports-v3/components/filters/SelectFilter.vue';
import { COL, sqlSum, sqlSumByDocType, getTableName } from '@/worker/db/sqlHelpers';
import {
	PermissionsGroup,
	ValueText,
} from '@/store/models.def';
import { TableItemFormatter } from '../../elements/charts/helpers/tableItemFormatter';
import moment from 'moment';
import gdbx from '@/store/modules/gdbx';
import IncludeCreditNoteToggle from '@/components/IncludeCreditNoteToggle.vue';
import IncludeDebitNoteToggle from '@/components/IncludeDebitNoteToggle.vue';
import SlPhModuleOnlyToggle from '@/components/SlPhModuleOnlyToggle.vue';
import settingx from '@/store/modules/settingx';

@Component({
	components: {
		TableFilterable,
		SelectFilter,
		IncludeCreditNoteToggle,
		IncludeDebitNoteToggle,
		SlPhModuleOnlyToggle,
	},
})
export default class SalesNewCustomerTable extends FilterWidget {
	public get permissionIds(): PermissionsGroup[] {
		return ['sales', 'customers'];
	}

	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
	> = [];

	public tableItems: any[] = [];
	public tableFields: any[] = [];

	public selectedMonth: number = moment().month();

	public get monthList() {
		const list: ValueText[] = [];
		for (let i = 0; i < 12; i++) {
			const object: ValueText = { value: i, text: '' };
			object.text = moment().month(i).format('MMM');
			list.push(object);
		}
		return list;
	}

	public get decimal() {
		return gdbx.numDecimal;
	}

	public get includeCreditNote() {
		return settingx.now.includeCreditNote;
	}

	public get includeDebitNote() {
		return settingx.now.includeDebitNote;
	}

	public get slPhModuleOnly() {
		return settingx.now.slPhModuleOnly;
	}

	public get exportFileName() {
		const formattedDate = [
			moment(this.currentMonthRange[0]).format('DD MMM YY'),
			moment(this.currentMonthRange[1]).format('DD MMM YY'),
		];
		return 'New Customer' + '_' + formattedDate[0] + ' to ' + formattedDate[1];
	}

	public get currentYear() {
		return moment(this.selectedAsOfDate).year();
	}

	public get currentMonth() {
		return this.selectedMonth;
	}

	public get currentMonthRange(): [number, number] {
		return [
			moment([this.currentYear, this.currentMonth]).startOf('month').valueOf(),
			moment([this.currentYear, this.currentMonth]).endOf('month').valueOf(),
		];
	}

	public get formattedMonth() {
		return moment([this.currentYear, this.currentMonth]).format('MMM');
	}

	public get expensiveHook() {
		const {
			selectedDateRange,
			selectedAgents,
			selectedStockItems,
			currentMonth,
			includeCreditNote,
			includeDebitNote,
			slPhModuleOnly,
		} = this;
		return JSON.stringify([
			selectedDateRange,
			selectedAgents,
			selectedStockItems,
			currentMonth,
			includeCreditNote,
			includeDebitNote,
			slPhModuleOnly,
		]);
	}

	public async expensiveCalc() {
		const jobId = this.currentJobId;
		this.generateLoadingText(0.2, 0.35);

		const queryHead = await this._ensureTablesLoaded(
			['globalCustomers', 'sales', 'creditNotes', 'customerTrades'] as any[],
		);
		this.checkInterrupted(jobId);

		const gcTable = getTableName('globalCustomers');
		const salesTable = getTableName('sales');
		const cnTable = getTableName('creditNotes');
		const arTable = getTableName('customerTrades');

		// Step 1: Get new customers (firstDeal within current month)
		const firstDealFilter = `TRY_CAST("FIRSTDEAL" AS BIGINT) BETWEEN ${this.currentMonthRange[0]} AND ${this.currentMonthRange[1]}`;
		const newCustomerRows = await queryHead.rawQuery(`
			SELECT "CODE" as code,
				COALESCE("FIRSTDEALAGENT", "AGENT", '') as firstDealAgent
			FROM ${gcTable}
			WHERE ${firstDealFilter}
		`);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.35, 0.9);

		const customerFirstDealAgent = new Map<string, string>();
		const newCustomerList: string[] = [];
		for (const r of newCustomerRows) {
			const code = String(r.code);
			customerFirstDealAgent.set(code, String(r.firstDealAgent || ''));
			newCustomerList.push(code);
		}

		this.tableFields = [
			{ key: 'company', sortable: true },
			{
				key: 'acquired_by',
				label: 'Acquired By',
				sortable: true,
			},
			{
				key: 'month',
				sortable: true,
			},
			{
				key: 'first_month_sales',
				sortable: true,
				formatter: TableItemFormatter.currencySplit,
			},
		];

		if (newCustomerList.length === 0) {
			this.tableItems = [];
			this.generateLoadingText(1);
			this.saveHistory('tableItems', 'tableFields');
			return;
		}

		// Step 2: Get sales by customer code
		const dateFilter = `TRY_CAST(${COL.DOCDATE} AS BIGINT) BETWEEN ${this.currentMonthRange[0]} AND ${this.currentMonthRange[1]}`;
		const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
		const customerFilter = ` AND ${COL.CODE} IN (${newCustomerList.map((c: string) => `'${escapeStr(c)}'`).join(', ')})`;

		if (this.slPhModuleOnly) {
			const docTypes = this.includeDebitNote ? ['IV', 'CS', 'DN'] : ['IV', 'CS'];
			const docTypeFilter = ` AND ${COL.DOCTYPE} IN (${docTypes.map((t) => `'${t}'`).join(', ')})`;

			const [salesByCode, cnByCode] = await Promise.all([
				queryHead.rawQuery(`
					SELECT ${COL.CODE} as code, ${sqlSum(COL.AMOUNT, 'totalAmt')}
					FROM ${salesTable}
					WHERE ${dateFilter}${customerFilter}${docTypeFilter}
					GROUP BY ${COL.CODE}
				`),
				queryHead.rawQuery(`
					SELECT ${COL.CODE} as code, ${sqlSum(COL.AMOUNT, 'totalAmt')}
					FROM ${cnTable}
					WHERE ${dateFilter}${customerFilter}
					GROUP BY ${COL.CODE}
				`),
			]);
			this.checkInterrupted(jobId);

			const cnMap = new Map<string, number>();
			for (const r of cnByCode) { cnMap.set(String(r.code), Number(r.totalAmt) || 0); }

			this.tableItems = salesByCode
				.filter((r: any) => (Number(r.totalAmt) || 0) !== 0)
				.map((r: any) => {
					const code = String(r.code);
					const salesAmt = Number(r.totalAmt) || 0;
					const cnAmt = this.includeCreditNote ? (cnMap.get(code) || 0) : 0;
					return {
						first_month_sales: salesAmt - cnAmt,
						company: gdbx.customerNames[code] || code,
						acquired_by: customerFirstDealAgent.get(code) || '',
						month: this.formattedMonth,
					};
				});
		} else {
			const arByCode = await queryHead.rawQuery(`
				SELECT ${COL.CODE} as code,
					${sqlSumByDocType(['IV'], COL.LOCALAMOUNT, 'iv_amount')},
					${sqlSumByDocType(['DN'], COL.LOCALAMOUNT, 'dn_amount')},
					${sqlSumByDocType(['CN'], COL.LOCALAMOUNT, 'cn_amount')}
				FROM ${arTable}
				WHERE ${dateFilter}${customerFilter}
				GROUP BY ${COL.CODE}
			`);
			this.checkInterrupted(jobId);

			this.tableItems = arByCode
				.map((r: any) => {
					const code = String(r.code);
					const iv = Number(r.iv_amount) || 0;
					const dn = this.includeDebitNote ? (Number(r.dn_amount) || 0) : 0;
					const cn = this.includeCreditNote ? (Number(r.cn_amount) || 0) : 0;
					return {
						first_month_sales: iv + dn - cn,
						company: gdbx.customerNames[code] || code,
						acquired_by: customerFirstDealAgent.get(code) || '',
						month: this.formattedMonth,
					};
				})
				.filter((item: any) => item.first_month_sales !== 0);
		}

		this.generateLoadingText(1);
		this.saveHistory('tableItems', 'tableFields');
	}

	public onHovering(value: boolean, filterId: string) {
		this.$emit('hovering', value, filterId);
	}
	public onEditing(value: boolean, filterId: string) {
		this.$emit('editing', value, filterId);
	}
}
